import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { useCurrency } from "@/context/CurrencyContext";
import { ShieldCheck, Plane, ArrowRight, Sparkles } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { courseTuitionUsd } from "@/lib/courseTuition";

// Rough grouping of subject_area → editorial "pathway" bucket. Purely
// presentational — the underlying shortage flag is the same for every row.
const PATHWAYS = [
  {
    key: "healthcare",
    label: "Healthcare",
    match: (d) => /nurs|medicine|optometry|health|paramedic/i.test(d.official_title),
    blurb: "The NHS is on every UK shortage list. Adult Nursing, Medicine and Optometry all lead directly to sponsorship-eligible roles.",
  },
  {
    key: "engineering",
    label: "Engineering",
    match: (d) => /engineer/i.test(d.official_title),
    blurb: "Aerospace, Mechanical, Automotive, Electronic and Petroleum engineers all sit on the UK Skilled Worker shortage roster.",
  },
  {
    key: "tech-security",
    label: "Tech & Security",
    match: (d) => /cyber|comput|data|artificial|software|robotic/i.test(d.official_title),
    blurb: "Cyber Security, Data Science and AI roles are chronically undersupplied — NCSC-certified degrees are the fastest track.",
  },
  {
    key: "other",
    label: "Other pathways",
    match: () => true,
    blurb: "Other regulated professions the UK actively sponsors.",
  },
];


function DegreeCard({ deg, uni, format }) {
  const offer = deg?.admissions?.offer_rate;
  const tuition = courseTuitionUsd(deg);
  const salary = deg?.outcomes?.median_starting_salary_usd;
  const psw = deg?.international?.psw_eligible_years;
  const accred = (deg?.accreditation || [])
    .map((a) => a?.body)
    .filter((b) => b && b !== "Institutional accreditor")
    .slice(0, 3);
  return (
    <Link
      to={`/degree/${deg.id}`}
      data-testid={`visa-friendly-degree-card-${deg.id}`}
      className="group border border-stone-200 rounded-lg p-5 bg-white hover:border-stone-500 transition-colors block"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-uppercase !text-[10px] text-stone-500">{uni?.name}</div>
          <div className="font-editorial text-lg mt-1 group-hover:underline underline-offset-4 decoration-stone-400">
            {deg.official_title}
          </div>
          <div className="text-xs text-stone-500 mt-1 font-mono">
            {deg.qualification} · {deg.duration_years} yr · {uni?.city}
          </div>
        </div>
        {typeof offer === "number" && (
          <div className="text-right shrink-0">
            <div className="label-uppercase !text-[9px] text-stone-500">Offer rate</div>
            <div className="font-mono text-lg mt-0.5">{Math.round(offer * 100)}%</div>
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Intl tuition</div>
          <div className="font-mono mt-0.5">{tuition ? format(tuition) : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Starting salary</div>
          <div className="font-mono mt-0.5">{salary ? format(salary) : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Post-study</div>
          <div className="font-mono mt-0.5">{psw ? `${psw} yr` : "—"}</div>
        </div>
      </div>

      {accred.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {accred.map((body) => (
            <Badge key={body} variant="secondary" className="text-[10px] font-normal">
              {body}
            </Badge>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-1.5 text-xs text-emerald-700">
        <ShieldCheck className="w-3.5 h-3.5" />
        <span>On the UK Shortage Occupation List — easier sponsorship after graduation.</span>
      </div>
    </Link>
  );
}


export default function UkVisaFriendlyDegrees() {
  const [data, setData] = useState({ degrees: [], universities: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/lists/uk-visa-friendly-degrees")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const claimed = new Set();
    const out = [];
    for (const p of PATHWAYS) {
      const rows = data.degrees.filter((d) => {
        if (claimed.has(d.id)) return false;
        if (p.key === "other") { claimed.add(d.id); return true; }
        const ok = p.match(d);
        if (ok) claimed.add(d.id);
        return ok;
      });
      if (rows.length) out.push({ ...p, rows });
    }
    return out;
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="Visa-friendly UK degrees · WhichUniversityAdvisor"
        description="Editorially-verified UK degrees that lead to Shortage Occupation List professions — the easiest post-study route to a Skilled Worker visa. Nursing, engineering, teaching, veterinary, cybersecurity and more."
        image="/og/uk-visa-friendly-degrees.png"
        path="/uk-visa-friendly-degrees"
      />
      {/* HERO */}
      <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="visa-friendly-hero">
        <div className="md:col-span-2">
          <div className="label-uppercase text-stone-500">The visa-first shortlist</div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-tight">
            Which UK degree gives you the <em>easiest</em> work visa after graduation?
          </h1>
          <p className="mt-5 text-stone-700 max-w-2xl leading-relaxed">
            The UK's <span className="font-semibold">Skilled Worker visa</span> is friendlier for roles
            on the government's <span className="font-semibold">Shortage Occupation List</span> — lower
            salary thresholds, faster CoS processing, and employers with a track record of sponsoring
            new graduates. Every degree below leads directly to a shortage-listed profession, and
            every entry is editorially verified against a 2027 UCAS course page.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Plane className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">2-yr Graduate Route + Skilled Worker route</span>
            </div>
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
              <span className="font-underground-sc tracking-wider">Verified against official course pages</span>
            </div>
          </div>
        </div>

        <aside
          data-testid="visa-friendly-counter"
          className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6"
        >
          <div className="label-uppercase !text-[10px] text-stone-500">Currently on this list</div>
          <div className="font-editorial text-5xl mt-2">{data.total}</div>
          <div className="text-sm text-stone-600 mt-1">
            editorial degrees across <span className="font-semibold">{Object.keys(data.universities).length}</span> UK universities
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs text-stone-500">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Sorted by offer rate — easiest offers first.</span>
          </div>
        </aside>
      </div>

      {/* EDITORIAL NOTE */}
      <div className="mt-10 border-l-2 border-stone-400 pl-4 py-2 text-sm text-stone-600 max-w-3xl italic"
           data-testid="visa-friendly-editorial-note">
        Editorial note: This list is informational and reflects the UK's 2025–26
        Immigration Salary List. Individual role eligibility can change with
        Home Office updates — always confirm the specific job code on gov.uk
        before applying. WhichUniversityAdvisor doesn't provide legal advice.
      </div>

      {/* GROUPED PATHWAYS — first-load flash suppressed (see other pages). */}
      {!loading && grouped.map((g) => (
        <section key={g.key} className="mt-14" data-testid={`visa-friendly-section-${g.key}`}>
          <div className="flex items-baseline justify-between border-b border-stone-200 pb-3">
            <div>
              <div className="label-uppercase text-stone-500">{g.rows.length} pathway{g.rows.length === 1 ? "" : "s"}</div>
              <h2 className="font-editorial text-2xl sm:text-3xl mt-1">{g.label}</h2>
            </div>
          </div>
          <p className="text-sm text-stone-600 mt-4 max-w-3xl">{g.blurb}</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.rows.map((d) => (
              <DegreeCard
                key={d.id}
                deg={d}
                uni={data.universities[d.university_id]}
                format={format}
              />
            ))}
          </div>
        </section>
      ))}

      {/* FOOTER CTA */}
      <div className="mt-20 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="label-uppercase text-stone-500">Not sure which one fits your grades?</div>
          <h3 className="font-editorial text-2xl mt-1">Run Advanced Search with your real profile.</h3>
          <p className="text-sm text-stone-600 mt-2 max-w-xl">
            We'll score every one of these against your grades, budget, and passport — and show only the ones you can actually get into.
          </p>
        </div>
        <Link
          to="/advanced"
          data-testid="visa-friendly-cta-advanced"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
        >
          Open Advanced Search <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
